import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plug, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConnectionsSettings() {
  const [open, setOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchConnections = async () => {
    try {
      const res = await axios.get(`${API}/connections`);
      setConnections(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchConnections(); }, []);

  const hasN8n = connections.some((c) => c.provider === 'n8n');

  const save = async () => {
    if (!baseUrl.trim() || !apiKey.trim()) { toast.error('Vul URL en API-key in'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/connections`, {
        provider: 'n8n',
        label: 'n8n',
        credentials: { base_url: baseUrl.trim(), api_key: apiKey.trim() },
      });
      toast.success('n8n gekoppeld');
      setBaseUrl(''); setApiKey('');
      setOpen(false);
      fetchConnections();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Koppelen mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="connections-btn">
          <Plug className="w-4 h-4" />
          Koppelingen
          {hasN8n && <Badge variant="secondary" className="bg-green-100 text-green-700 ml-1"><Check className="w-3 h-3" /></Badge>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>n8n koppelen</DialogTitle>
          <DialogDescription>
            laibel gebruikt jouw n8n als cron-motor. LLM en mail draaien in laibel — je API-key wordt versleuteld opgeslagen.
          </DialogDescription>
        </DialogHeader>
        {hasN8n && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded p-3">
            <Check className="w-4 h-4" /> n8n is al gekoppeld. Opnieuw invullen overschrijft de bestaande koppeling.
          </div>
        )}
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="n8n-url">n8n instance-URL</Label>
            <Input id="n8n-url" placeholder="https://n8n.jouwdomein.nl" value={baseUrl}
                   onChange={(e) => setBaseUrl(e.target.value)} data-testid="n8n-url-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="n8n-key">n8n API-key</Label>
            <Input id="n8n-key" type="password" placeholder="n8n_api_..." value={apiKey}
                   onChange={(e) => setApiKey(e.target.value)} data-testid="n8n-key-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuleer</Button>
          <Button onClick={save} disabled={saving} data-testid="n8n-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Koppel n8n
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
